import {
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
  Skeleton,
} from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { CommonDialog } from "../../../../components/common/CommonDialog";

import dayjs from "dayjs";
import { isMobile } from "react-device-detect";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FormButtonGroup, FormSelectInput } from "../../../../components/form";
import { currentFeatures, endpoints, oids } from "../../../../configs/Settings";
import { useSessionContext } from "../../../../contexts/SessionContext";
import { GetSchedule, PostAppointment } from "../../../../services/fhir";
import { PostWhereby } from "../../../../services/whereby/PostWhereby";
import { useFhirData } from "../../../../utils/useFhirData";
import { useFormatter } from "../../../../utils/useFormatter";
import { PostPaymentNotice } from "@/services/fhir/paymentnotice/PostPaymentNotice";
import {
  buildClinicalAutomationTriggerInputFromAppointmentForm,
  buildScheduleManagerRequesterReference,
  triggerPreConsultationAutomation,
} from "@/services/crm/triggerClinicalAutomation";
import { DomicilioAddressStep } from "../../../schedulemanager/components/creatappointmentdialog/DomicilioAddressStep";

interface Iprops {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  location: fhir5.Location;
  scheduleId: string;
  slot: any;
  setRefetchSlots: Dispatch<SetStateAction<boolean>>;
  scheduleCompleted?: any;
}

export function ConfirmAppointmentDialog({
  isOpen,
  setIsOpen,
  location,
  scheduleId,
  slot,
  setRefetchSlots,
  scheduleCompleted,
}: Iprops) {
  const navigate = useNavigate();
  const { user, patientRelated } = useSessionContext();
  
  // Funções auxiliares (movidas para o início)
  function isExtensionVirtualService(extension: fhir5.Extension) {
    return (
      extension.url ===
        "https://fluxmed.com.br/fhir/StructureDefinition/virtual-service" &&
      extension.valueBoolean === true
    );
  }

  function isVirtualService(loc: fhir5.Location) {
    return !!loc?.extension?.find(isExtensionVirtualService);
  }

  /** Retorna o canal de atendimento da location (teleatendimento, chat, domicilio…) */
  function getAttendanceChannel(loc: fhir5.Location): string | undefined {
    return (loc?.extension?.find((ext) =>
      ext.url?.includes("attendance-channel")
    ) as any)?.valueCode;
  }

  // Derivados do tipo de atendimento da location
  const attendanceChannel = getAttendanceChannel(location);
  const isDomicilio = attendanceChannel === "domicilio";
  const isChat = attendanceChannel === "chat";
  const isVirtual = isVirtualService(location);

  // Estados
  const [loading, setLoading] = useState(false);
  const [practitioner, setPractitioner] = useState<fhir5.Reference>();
  const [healthcareService, setHealthcareService] = useState<fhir5.Reference>();
  const [locationActor, setLocationActor] = useState<fhir5.Reference>();
  const [petsLoading, setPetsLoading] = useState(false); // Controla loading do select de pets

  // Controla o passo atual do dialog:
  //   "confirm"  — tela de confirmação normal
  //   "address"  — formulário de endereço (apenas para domicílio)
  const [dialogStep, setDialogStep] = useState<"confirm" | "address">(
    // Define o passo inicial baseado no tipo de atendimento
    isDomicilio ? "address" : "confirm"
  );

  const { control, watch, setValue, trigger } = useForm<any>();
  const selectedPatient = watch("patients");
  const { formatCPF, extractNumericValue } = useFormatter;

  const auxAddress = location?.address?.line
    ? location.address.line.join(" ,")
    : [
        location?.address?.city,
        location?.address?.state,
        location?.address?.postalCode,
      ]
        .filter((el) => el)
        .join(", ");

  function handleClose() {
    setIsOpen(false);
    // Reseta para o passo inicial quando fecha o modal
    setDialogStep(isDomicilio ? "address" : "confirm");
  }


  // Reseta o passo do dialog apenas quando o modal é aberto (não a cada re-render)
  useEffect(() => {
    if (isOpen) {
      // Define o passo inicial sem causar re-renders desnecessários
      const initialStep = isDomicilio ? "address" : "confirm";
      setDialogStep(initialStep);
    }
  }, [isOpen]); // Remove isDomicilio para evitar mudanças durante uso do modal

  // Repassa o CPF do paciente selecionado para o form (DomicilioAddressStep usa esse campo)
  useEffect(() => {
    if (selectedPatient) setValue("cpf", selectedPatient);
  }, [selectedPatient, setValue]);

  // Controla o loading de pets para FluxVet apenas no passo de confirmação
  useEffect(() => {
    if (!currentFeatures?.defaultApp && dialogStep === "confirm") {
      // Inicia loading quando entra no passo de confirmação
      if (patientRelated === undefined) {
        setPetsLoading(true);
      } else {
        // Para o loading quando patientRelated é definido
        setPetsLoading(false);
      }
    } else {
      // Sempre desliga loading quando não está no passo de confirmação
      setPetsLoading(false);
    }
  }, [patientRelated, dialogStep]);

  const DependentList =
    patientRelated
      ?.map((el: fhir5.Patient | any) => ({
        name: el?.name?.[0]?.text,
        identifier: currentFeatures?.defaultApp
          ? formatCPF(el?.identifier || "")
          : el?.identifier,
        isValid: !el?.period?.end,
      }))
      ?.filter((related: any) => related?.isValid) || [];

  // Quando defaultApp é false, não incluir o usuário nas opções (apenas pets)
  // Quando defaultApp é true, incluir o usuário como primeira opção
  const PatientOptions: Array<any> = [
    ...(currentFeatures?.defaultApp
      ? [{ display: user.name, value: user.username }]
      : []),
    ...DependentList?.map((dependent: any) => ({
      display: dependent.name,
      value: dependent.identifier,
    })),
    // Para FluxVet: se não há pets e não está carregando, mostra mensagem
    ...(!currentFeatures?.defaultApp && DependentList?.length === 0 && !petsLoading
      ? [{ display: "Nenhum pet encontrado", value: "no_pets" }]
      : []),
  ];

  const onSubmit = async () => {
    setLoading(true);
    const selectedOption = PatientOptions?.find(
      (option) => option.value === selectedPatient
    );

    // Para FluxVet: impede envio se não há pet selecionado ou se não há pets disponíveis
    if (!currentFeatures?.defaultApp && (!selectedOption || selectedOption.value === "no_pets")) {
      setLoading(false);
      return;
    }

    const auxServiceCost = scheduleCompleted?.resource?.extension?.find(
      (el: any) => el?.url.includes("service-cost")
    )?.valueDecimal;

    // Lê o endereço preenchido no DomicilioAddressStep (campo domicilioAddress do form)
    const domicilioAddressForm = watch("domicilioAddress");

    // Para FluxVet (defaultApp=false), busca os dados do pet selecionado na lista de patientRelated
    // Para FluxMed (defaultApp=true), usa os dados do usuário/responsável
    let selectedPatientData = null;
    if (!currentFeatures?.defaultApp) {
      // FluxVet: encontra o pet selecionado na lista de patientRelated
      selectedPatientData = patientRelated?.find((pet: any) => 
        pet.identifier === selectedOption?.value
      );
    }

    let data: any = {
      cpf: selectedOption?.value.replace(/[.-]/g, "") || user?.username,
      typeService: "CHECKUP",
      healthCareServiceActor: healthcareService,
      professionalActor: practitioner,
      patientActor: {
        reference: currentFeatures?.defaultApp
          ? `Patient/${oids.cpf}-${
              selectedOption?.value?.replace(/[.-]/g, "") || user?.username
            }`
          : `Patient/${selectedPatientData?.identifier || selectedOption?.value || user?.username}`,
        display: selectedPatientData?.name?.[0]?.text || selectedOption?.display || user?.name,
      },
      locationActor,
      slotInfo: {
        id: slot.id,
        start: slot.start,
        end: slot.end,
      },
      // Inclui endereço no appointment se for domicílio
      ...(isDomicilio && domicilioAddressForm ? { domicilioAddress: domicilioAddressForm } : {}),
    };

    // Teleatendimento e Chat compartilham o mesmo fluxo: criação de sala Whereby
    if (isVirtual) {
      try {
        const auxData = {
          endDate: dayjs(slot?.end).format("YYYY-MM-DDTHH:mm:ss-03:00"),
          roomNamePrefix: `${endpoints?.AppName}-whereby`,
          roomNamePattern: "human-short",
        };
        const response = await PostWhereby(auxData);

        data = {
          ...data,
          ...(auxServiceCost && { serviceCost: auxServiceCost }),
          virtualService: [
            {
              addressUrl: response.roomUrl,
              sessionKey: response.meetingId || "",
            },
          ],
        };
      } catch (err) {
        console.log("err", err);
      }
    } else {
      data = {
        ...data,
        ...(auxServiceCost && { serviceCost: auxServiceCost }),
      };
    }

    try {
      const auxAppointment = await PostAppointment(data);
      if (auxServiceCost) {
        const dataPaymentNotice: any = {
          resourceType: "PaymentNotice",
          status: "active",
          extension: [
            {
              url: "http://fluxmed.com.br/fhir/StructureDefinition/AppointmentReference",
              valueReference: {
                reference: `Appointment/${auxAppointment?.id}`,
              },
            },
            {
              url: "http://fluxmed.com.br/fhir/StructureDefinition/OriginalServiceCost",
              valueDecimal: extractNumericValue(data?.serviceCost),
            },
          ],
          identifier: [
            {
              system: "https://fluxmed.com.br/fhir/identifier/organization",
              value: `${location?.managingOrganization?.reference}`,
            },
            {
              system: "https://fluxmed.com.br/fhir/identifier/location",
              value: `Location/${location?.id}`,
            },
          ],
          response: {
            reference: `${data?.patientActor?.reference}`,
          },
          request: {
            reference: `Slot/${data?.slotInfo?.id}`,
          },
          created: dayjs().format(),
          reporter: { reference: `${data?.professionalActor?.reference}` },
          recipient: {
            reference: `${location?.managingOrganization?.reference}`,
          },
          amount: {
            value: `${extractNumericValue(data?.serviceCost)}`,
            currency: "BRL",
          },
        };
        await PostPaymentNotice(dataPaymentNotice);
      }

      // Pré-consulta automática quando a consulta já nasce confirmada (booked).
      if (auxAppointment?.status === "booked") {
        try {
          const organizationId =
            location?.managingOrganization?.reference?.split("/")?.[1] ??
            location?.managingOrganization?.reference;
          const triggerInput =
            buildClinicalAutomationTriggerInputFromAppointmentForm({
              organizationId,
              professionalActorReference: practitioner?.reference,
              patientActorReference: data.patientActor.reference,
              patientDisplayName: data.patientActor.display,
              patientCpf: data.cpf,
              appointmentId: auxAppointment.id,
              appointmentStartAtIso: auxAppointment.start ?? slot.start,
              requesterReference: buildScheduleManagerRequesterReference({
                organizationId,
                practitionerUserId: user?.username,
              }),
            });
          if (triggerInput) {
            await triggerPreConsultationAutomation(triggerInput);
          }
        } catch (automationError) {
          console.error("[automationTrigger] pré-consulta:", automationError);
        }
      }

      navigate("/");
      toast.success("Consulta marcada com sucesso!");
      setRefetchSlots((prev) => !prev);
      handleClose();
    } catch (err) {
      toast.error("Não foi possível preencher a vaga!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    async function asyncCall() {
      const schedule = await GetSchedule({ id: scheduleId });
      setHealthcareService(
        useFhirData.findActor(schedule.actor, "HealthcareService")
      );
      setPractitioner(useFhirData.findActor(schedule.actor, "Practitioner"));
      setLocationActor(useFhirData.findActor(schedule.actor, "Location"));
    }
    if (scheduleId) asyncCall();
  }, []);

  // Define o valor inicial do campo de pacientes
  useEffect(() => {
    if (PatientOptions?.length > 0 && !selectedPatient && !petsLoading) {
      const initialValue = currentFeatures?.defaultApp
        ? user?.username
        : PatientOptions.find(option => 
            option.value !== "no_pets" && option.value !== "loading"
          )?.value || PatientOptions[0]?.value;

      if (initialValue && initialValue !== "no_pets") {
        setValue("patients", initialValue);
      }
    }
  }, [PatientOptions, selectedPatient, setValue, user?.username, petsLoading]);

  // Rótulo do tipo de atendimento exibido na confirmação
  const attendanceTypeLabel =
    isDomicilio ? "Atendimento a Domicílio" :
    isChat      ? "Chat" :
    isVirtual   ? "Teleatendimento" :
                  "Presencial";

  return (
    <CommonDialog
      handleClose={handleClose}
      isOpen={isOpen}
      mobileView={isMobile}
    >
      <DialogTitle textAlign="left">
        {dialogStep === "address" ? "Endereço do atendimento" : "Confirmar agendamento"}
      </DialogTitle>

      <DialogContent>
        {/* ── PASSO 1: Endereço (apenas para domicílio) ── */}
        {dialogStep === "address" && (
          <DomicilioAddressStep
            control={control}
            setValue={setValue}
            trigger={trigger}
            onNext={() => setDialogStep("confirm")}
            onBack={() => setIsOpen(false)}
          />
        )}

        {/* ── PASSO 2 (ou único): Confirmação ── */}
        {dialogStep === "confirm" && (
          <>
            <DialogContentText
              paddingBottom={{ xs: 2, md: 3 }}
              color="neutral700.main"
            >
              Você solicitou o agendamento de uma consulta com as seguintes
              caracteristicas:
            </DialogContentText>

            {(DependentList?.length > 0 || !currentFeatures?.defaultApp) ? (
              <Stack marginBottom={4}>
                {petsLoading && !currentFeatures?.defaultApp && dialogStep === "confirm" ? (
                  // Skeleton para FluxVet enquanto carrega pets (apenas no passo de confirmação)
                  <Stack spacing={1}>
                    <Skeleton variant="text" width={120} height={20} />
                    <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  </Stack>
                ) : (
                  <FormSelectInput
                    name="patients"
                    control={control}
                    label={
                      currentFeatures?.defaultApp
                        ? "Nome do paciente"
                        : "Selecione um pet"
                    }
                    selectOptions={PatientOptions}
                    required
                  />
                )}
              </Stack>
            ) : null}

            <Stack>
              <Stack
                minWidth="100%"
                justifyContent="left"
                spacing={{ xs: 4, md: 19 }}
                direction="row"
              >
                <Stack>
                  <Typography variant="body1" fontWeight={600} color="neutral700.main" paddingBottom={1}>
                    Data e hora
                  </Typography>
                  <Typography variant="body1" color="neutral700.main">
                    Dia: {dayjs(slot.start).format("DD/MM/YYYY")}
                  </Typography>
                  <Typography variant="body1" color="neutral700.main">
                    Horário: {dayjs(slot.start).format("HH:mm")}
                  </Typography>
                </Stack>
                <Stack>
                  <Typography variant="body1" fontWeight={600} color="neutral700.main" paddingBottom={1}>
                    Serviço de saúde:
                  </Typography>
                  <Typography variant="body1" color="neutral700.main">
                    {healthcareService?.display}
                  </Typography>
                </Stack>
              </Stack>

              <Stack
                minWidth="100%"
                justifyContent="left"
                spacing={{ xs: 1, md: 19 }}
                direction={{ xs: "column", md: "row" }}
                paddingTop={{ xs: 1, md: 3 }}
              >
                <Stack>
                  <Typography variant="body1" fontWeight={600} color="neutral700.main" paddingBottom={1}>
                    Clínica:
                  </Typography>
                  <Typography variant="body1" color="neutral700.main">
                    {location.name}
                  </Typography>
                </Stack>
                {!isDomicilio && (
                  <Stack>
                    <Typography variant="body1" fontWeight={600} color="neutral700.main" paddingBottom={1}>
                      Localização:
                    </Typography>
                    <Typography variant="body1" color="neutral700.main">
                      {auxAddress}
                    </Typography>
                  </Stack>
                )}
              </Stack>

              <Stack
                minWidth="100%"
                justifyContent="left"
                spacing={{ xs: 1, md: 19 }}
                direction={{ xs: "column", md: "row" }}
                paddingTop={{ xs: 1, md: 3 }}
              >
                <Stack>
                  <Typography variant="body1" fontWeight={600} color="neutral700.main" paddingBottom={1}>
                    Tipo de atendimento:
                  </Typography>
                  <Typography variant="body1" color="neutral700.main">
                    {attendanceTypeLabel}
                  </Typography>
                </Stack>
                <Stack>
                  <Typography variant="body1" fontWeight={600} color="neutral700.main" paddingBottom={1}>
                    Profissional:
                  </Typography>
                  <Typography variant="body1" color="neutral700.main">
                    {practitioner?.display}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            <Stack minWidth="100%" mt={3} direction="row" justifyContent="space-between" spacing={2}>
              <FormButtonGroup
                onGoBackButtonClick={() =>
                  isDomicilio ? setDialogStep("address") : setIsOpen(false)
                }
                onNextButtonClick={onSubmit}
                goBackButtonText={isDomicilio ? "Voltar" : "Cancelar"}
                nextButtonText="Confirmar"
                loading={loading}
              />
            </Stack>
          </>
        )}
      </DialogContent>
    </CommonDialog>
  );
}
